import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, Banner, Text } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";

interface WorkSecurityUnassignConfirmModalProps {
  userName: string;
  onConfirm: () => Promise<void>;
}

export default function WorkSecurityUnassignConfirmModal({
  userName,
  onConfirm,
}: WorkSecurityUnassignConfirmModalProps) {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();

  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      setErrMsg(null);
      await onConfirm();
      // Close modal on success
      dispatch(setIsModalxOpen(false));
      dispatch(setDestroy());
    } catch (e: any) {
      console.error(e);
      setErrMsg(e?.response?.data?.message || t("failed_to_remove_user", { defaultValue: "Failed to remove user." }));
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  return (
    <div className="space-y-4">
      {errMsg && (
        <Banner tone="critical">
          {errMsg}
        </Banner>
      )}

      <div className="text-center py-2">
        <Text as="p" variant="bodyMd">
          {t("confirm_unassign_user_description", {
            defaultValue: `Are you sure you want to remove ${userName} from this role? All their pending signature requests for this role will be deleted.`,
            userName,
          })}
        </Text>
      </div>

      <div className="flex justify-end gap-3 mt-4 border-t border-gray-150 pt-4">
        <Button onClick={closeModal} disabled={isLoading}>
          {t("No", { defaultValue: "No" })}
        </Button>
        <Button onClick={handleConfirm} loading={isLoading} variant="primary" tone="critical">
          {t("Yes", { defaultValue: "Yes, Remove" })}
        </Button>
      </div>
    </div>
  );
}
